import React, { useState, useEffect, useCallback } from "react";
import { botAnalyticsAPI } from "../services/api";
import { Radar, RefreshCw, TrendingUp, Newspaper, Zap, Clock } from "lucide-react";

export default function LiveDiscoveries() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await botAnalyticsAPI.getLiveDiscoveries();
      setData(res);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 120000); return () => clearInterval(iv); }, [fetchData]);

  if (!data) {
    return (
      <div className="glass-card rounded-xl p-4 border border-slate-700/30 animate-pulse" data-testid="live-discoveries-loading">
        <div className="h-4 bg-slate-700/50 rounded w-1/3 mb-3" />
        <div className="h-16 bg-slate-700/30 rounded" />
      </div>
    );
  }

  const movers = data.movers || [];
  const newsMovers = data.news_movers || [];
  const updatedAt = data.updated_at ? new Date(data.updated_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="glass-card rounded-xl p-4 border border-orange-500/20 space-y-3" data-testid="live-discoveries-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Radar className="w-3.5 h-3.5 text-orange-400" />
          </div>
          <h3 className="text-sm font-black text-white uppercase tracking-wider">Live Discovery Engine</h3>
          <span className="text-[10px] text-slate-500">{data.total_scanned || 0} tickers scannes</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{updatedAt}</span>
          <button onClick={fetchData} disabled={loading} className="p-1 rounded bg-slate-700/50 text-slate-400 hover:text-white disabled:opacity-40" data-testid="refresh-discoveries">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Top Movers Grid */}
      {movers.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp className="w-3 h-3 text-emerald-400" />
            <span className="text-[11px] font-bold text-emerald-400 uppercase">Top Movers Temps Reel</span>
            <span className="text-[10px] text-slate-500">({movers.length})</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5">
            {movers.slice(0, 10).map((m, i) => (
              <div key={m.ticker} className="bg-slate-800/60 rounded-lg p-2 border border-slate-700/40 hover:border-emerald-500/30 transition-all" data-testid={`mover-${m.ticker}`}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs font-bold text-white">{m.ticker}</span>
                  {i < 3 && <Zap className="w-2.5 h-2.5 text-orange-400" />}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-400 text-xs font-bold">+{m.change_pct}%</span>
                  <span className="text-[10px] text-slate-500">${m.price}</span>
                </div>
                {m.vol_ratio > 0 && (
                  <span className={`text-[9px] ${m.vol_ratio >= 2 ? "text-orange-400" : "text-slate-500"}`}>
                    Vol: {m.vol_ratio}x
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* News-Driven Tickers */}
      {newsMovers.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Newspaper className="w-3 h-3 text-cyan-400" />
            <span className="text-[11px] font-bold text-cyan-400 uppercase">Catalyseurs News</span>
            <span className="text-[10px] text-slate-500">({newsMovers.length})</span>
          </div>
          <div className="space-y-1">
            {newsMovers.slice(0, 6).map((n) => (
              <div key={n.ticker} className="bg-slate-800/40 rounded-lg px-2.5 py-1.5 border border-slate-700/30 flex items-center gap-2" data-testid={`news-mover-${n.ticker}`}>
                <span className="text-xs font-bold text-white min-w-[50px]">{n.ticker}</span>
                <span className="text-[10px] text-cyan-400 font-mono min-w-[20px]">{n.mentions}x</span>
                <span className="text-[10px] text-slate-400 truncate">{n.headline || "Multiple articles"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {movers.length === 0 && newsMovers.length === 0 && (
        <div className="text-center py-6 text-slate-500 text-xs">
          En attente du prochain scan...
        </div>
      )}
    </div>
  );
}
